import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { socket } from '../services/socket';
import { useAuth } from './AuthContext';
import { debugLog, debugWarn } from '../utils/logger';
import VideoCallModal from '../components/common/VideoCallModal';

/**
 * Receptor global de videollamadas.
 *
 * Antes, el listener de 'incomingCall' vivía DENTRO de VideoCallModal, y ese
 * modal solo se montaba cuando el propio usuario pulsaba "Videollamada". Si el
 * médico timbraba mientras el paciente estaba en el chat, el backend entregaba
 * el evento a su socket y el navegador lo descartaba en silencio por no tener
 * ningún listener: al paciente "no le salía nada".
 *
 * Este provider escucha el timbre siempre, monta el único VideoCallModal de la
 * app y le pasa la señal ya recibida por `initialIncomingCall` para que no se
 * pierda el SDP de la oferta entre el evento y el montaje del modal.
 */

const VideoCallContext = createContext(null);

export const useVideoCall = () => {
  const ctx = useContext(VideoCallContext);
  if (!ctx) throw new Error('useVideoCall debe usarse dentro de <VideoCallProvider>');
  return ctx;
};

// Contraparte mínima que necesita VideoCallModal: un id de usuario al que
// devolver la llamada y un nombre que mostrar.
const callerAsOtherUser = (data) => ({
  _id: data.fromUserId,
  profile: { firstName: data.fromName || 'Participante' }
});

export function VideoCallProvider({ children }) {
  const { user } = useAuth();
  // { appointment, otherUser, initialIncomingCall }
  const [call, setCall] = useState(null);

  // Espejos para leer el estado actual desde un listener registrado una sola vez.
  const callRef = useRef(null);
  useEffect(() => { callRef.current = call; }, [call]);

  const myUserIdRef = useRef(null);
  useEffect(() => { myUserIdRef.current = user?._id || user?.id || null; }, [user]);

  const openVideoCall = useCallback((appointment, otherUser) => {
    if (!appointment?._id) {
      debugWarn('⚠️ [VideoCallProvider] openVideoCall sin cita válida');
      return;
    }
    setCall({ appointment, otherUser, initialIncomingCall: null });
  }, []);

  const closeVideoCall = useCallback(() => setCall(null), []);

  useEffect(() => {
    const onIncomingCall = (data) => {
      if (!data?.appointmentId) return;

      // Una llamada de uno mismo no abre nada: si el otro extremo se resolviera
      // mal y alguien acabara llamándose, el síntoma sería un timbre entrante
      // en la propia pantalla en vez de un error, que despista mucho más.
      if (data.fromUserId && String(data.fromUserId) === String(myUserIdRef.current)) {
        debugWarn('⚠️ [VideoCallProvider] Timbre de uno mismo, ignorado');
        return;
      }

      const current = callRef.current;

      // Modal ya abierto: su propio listener maneja la señal. Si además fuera de
      // otra cita, no interrumpimos una consulta en curso.
      if (current) {
        if (String(current.appointment?._id) !== String(data.appointmentId)) {
          debugWarn('⚠️ [VideoCallProvider] Timbre de otra cita ignorado:', data.appointmentId);
        }
        return;
      }

      debugLog('\n📞 [VideoCallProvider] Timbre global de:', data.fromName, '| cita:', data.appointmentId);
      setCall({
        appointment: { _id: data.appointmentId },
        otherUser: callerAsOtherUser(data),
        initialIncomingCall: data
      });
    };

    socket.on('incomingCall', onIncomingCall);
    return () => socket.off('incomingCall', onIncomingCall);
  }, []);

  return (
    <VideoCallContext.Provider value={{ openVideoCall, closeVideoCall, activeCall: call }}>
      {children}
      {call && (
        <VideoCallModal
          // Remontar al cambiar de cita: el modal guarda el peer y los refs de
          // señalización en estado interno atado a un único roomId.
          key={call.appointment._id}
          appointment={call.appointment}
          otherUser={call.otherUser}
          initialIncomingCall={call.initialIncomingCall}
          onClose={closeVideoCall}
        />
      )}
    </VideoCallContext.Provider>
  );
}

export default VideoCallContext;
